import * as React from 'react';
import {
  MemberRegistration,
  MemberRegistrationData,
  MemberRegistrationDataResponse,
  RegistrationOrderStatus,
} from './types';
import { useGet } from 'core/api';

type Props = {
  orgId: number;
  memberId: number;
};

export const useMemberRegistrationData = ({ orgId, memberId }: Props) => {
  const { data: memberRegistrationData, loading } =
    useGet<MemberRegistrationDataResponse>(
      `/organizations/${orgId}/members/${memberId}/registrations`
    );

  const registrationData = React.useMemo(() => {
    if (!memberRegistrationData || !memberRegistrationData.data) return [];
    const { data } = memberRegistrationData;
    return data.map((item) => mapToMemberRegistration(item));
  }, [memberRegistrationData]);

  const registrationKeyInsights = React.useMemo(() => {
    if (!registrationData || registrationData.length === 0) return null;

    const outstandingAmount = registrationData.reduce((acc, registration) => {
      if (registration.balance > 0) {
        return acc + registration.balance;
      }
      return acc;
    }, 0);

    const scheduledAmount = registrationData.reduce(
      (acc, registration) => acc + registration.scheduledPaymentsAmount,
      0
    );

    const paidAmount = registrationData.reduce(
      (acc, registration) => acc + (registration.total - registration.balance),
      0
    );

    return { outstandingAmount, scheduledAmount, paidAmount };
  }, [registrationData]);

  return { registrationData, registrationKeyInsights, loading };
};

const mapToMemberRegistration = (
  registrationDataItem: MemberRegistrationData
): MemberRegistration => ({
  total: registrationDataItem.total,
  type: registrationDataItem.type as 'reg1' | 'reg2',
  balance: registrationDataItem.balance,
  orderId: registrationDataItem.order_id.toString(),
  seasonId: registrationDataItem.season_id,
  programName: registrationDataItem.program_name,
  seasonName: registrationDataItem.season_name,
  programId: registrationDataItem.program_id,
  programSeasonName: registrationDataItem.program_season_name,
  registrationFormName: registrationDataItem.registration_form_name,
  scheduledPaymentsAmount: registrationDataItem.scheduled_payments_amount,
  completedInstallmentsCount: registrationDataItem.completed_installments_count,
  installmentPlanPaymentCount:
    registrationDataItem.installment_plan_payment_count,
  orderStatus: registrationDataItem.order_status as RegistrationOrderStatus,
  participantNames: registrationDataItem.participant_names.join(', '),
  registrantName: registrationDataItem.registrant_name,
  registrationDate: registrationDataItem.registration_date,
  divisionId: registrationDataItem.division_id,
  registrationFormId: registrationDataItem.registration_form_id,
});
